import { Component } from '@theme/component';
import { DialogComponent } from '@theme/dialog';
import { ThemeEvents } from '@theme/events';

class QuickAddCustom extends Component {
  connectedCallback() {
    super.connectedCallback();

    this.openButton = this.querySelector('[data-open-quick-add-custom]');
    this.productJsonEl = this.querySelector('[data-product-json]');

    if (this.openButton) {
      this.openButton.addEventListener('click', this.handleOpen);
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    if (this.openButton) {
      this.openButton.removeEventListener('click', this.handleOpen);
    }
  }

  handleOpen = () => {
    if (!this.productJsonEl) return;

    let productData = null;

    try {
      productData = JSON.parse(this.productJsonEl.textContent);
    } catch (error) {
      console.error('Failed to parse quick add custom product JSON', error);
      return;
    }

    const dialog = document.getElementById('quick-add-dialog-custom');
    if (!(dialog instanceof QuickAddDialogCustom)) return;

    dialog.setProduct(productData);
    dialog.showDialog();

    this.openButton?.setAttribute('aria-expanded', 'true');
  };
}

if (!customElements.get('quick-add-custom')) {
  customElements.define('quick-add-custom', QuickAddCustom);
}

class QuickAddDialogCustom extends DialogComponent {
  connectedCallback() {
    super.connectedCallback();

    this.closeButton = this.querySelector('[data-close-quick-add-custom]');
    this.titleEl = this.querySelector('[data-quick-add-custom-title]');
    this.imageEl = this.querySelector('[data-quick-add-custom-image]');
    this.sizesEl = this.querySelector('[data-quick-add-custom-sizes]');
    this.statusEl = this.querySelector('[data-quick-add-custom-status]');

    if (this.closeButton) {
      this.closeButton.addEventListener('click', this.closeDialog);
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    if (this.closeButton) {
      this.closeButton.removeEventListener('click', this.closeDialog);
    }
  }

  setProduct(product) {
    this.product = product;

    if (this.titleEl) {
      this.titleEl.textContent = product.title || '';
    }

    const firstAvailableVariantImage =
      product.variants?.find((variant) => variant.available && variant.featured_image)?.featured_image;

    const imageSrc = firstAvailableVariantImage || product.image || '';

    if (this.imageEl) {
      this.imageEl.src = imageSrc;
      this.imageEl.alt = product.title || '';
    }

    this.renderSizes();
  }

  renderSizes() {
    if (!this.sizesEl || !this.product) return;

    this.sizesEl.innerHTML = '';

    const { variants, size_option_index } = this.product;

    const seen = new Set();

    variants.forEach((variant) => {
      let sizeValue = '';

      if (size_option_index === 0) sizeValue = variant.option1;
      if (size_option_index === 1) sizeValue = variant.option2;
      if (size_option_index === 2) sizeValue = variant.option3;

      if (!sizeValue || seen.has(`${sizeValue}-${variant.id}`)) return;

      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'quick-add-modal-custom__size-button';
      button.textContent = sizeValue;
      button.dataset.variantId = variant.id;

      if (!variant.available) {
        button.disabled = true;
      }

      button.addEventListener('click', () => {
        this.addVariantToCart(variant.id, sizeValue);
      });

      this.sizesEl.appendChild(button);
      seen.add(`${sizeValue}-${variant.id}`);
    });
  }

  async addVariantToCart(variantId, sizeValue) {
    try {
      this.setStatus(`Adding size ${sizeValue} to cart`);
  
      const response = await fetch(`${window.Shopify.routes.root}cart/add.js`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify({
          items: [
            {
              id: Number(variantId),
              quantity: 1,
            },
          ],
        }),
      });
  
      const data = await response.json();
  
      if (!response.ok) {
        throw new Error(data.description || data.message || 'Failed to add to cart');
      }
  
      document.dispatchEvent(
        new CustomEvent(ThemeEvents.cartUpdate, {
          bubbles: true,
          detail: {
            data: {
              source: 'quick-add-custom',
              cart: data,
              didError: false,
            },
          },
        })
      );
  
      this.setStatus(`Added size ${sizeValue} to cart`);
      this.showNotification();
      
      setTimeout(() => {
        this.closeDialog();
      }, 800);
      
    } catch (error) {
      console.error(error);
      this.setStatus('Could not add item to cart');
    }
  }

  setStatus(message) {
    if (this.statusEl) {
      this.statusEl.textContent = message;
    }
  }

  showNotification() {
    const notification = document.getElementById('quick-add-notification');
    if (notification) {
      notification.classList.add('show');
      setTimeout(() => {
        notification.classList.remove('show');
      }, 2500);
    }
  }
}

if (!customElements.get('quick-add-dialog-custom')) {
  customElements.define('quick-add-dialog-custom', QuickAddDialogCustom);
}